import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useCapabilities } from './CapabilityProvider';
import Icon from './ui/icons/Icon';

// Navigation structure with grouping
// Icons use SVG icon system (Icon component)
const navStructure = {
  pinned: [
    { path: '/admin', label: 'Home', icon: 'home', capability: null },
    { path: '/modules/analytics', label: 'Analytics', icon: 'data', capability: 'admin:analytics:read' },
    { path: '/modules/clc-properties', label: 'Property 360', icon: 'home', capability: 'admin:clc-properties:read' },
    { path: '/modules/serenity', label: 'Serenity Intelligence', icon: 'data', capability: 'admin:serenity:read' },
    { path: '/modules/booking-workspaces', label: 'Workspaces', icon: 'users', capability: 'admin:workspaces:read' },
    { path: '/modules/development-hub', label: 'Development Hub', icon: 'work', capability: 'admin:development-hub:read' },
    { path: '/modules/clc-communications/queue', label: 'Communications', icon: 'communications', capability: 'admin:clc-communications:read' },
    { path: '/modules/tax-review', label: 'Tax Review', icon: 'audit', capability: 'admin:modules:read' },
  ],
  groups: [
    {
      id: 'operations',
      label: 'Operations',
      icon: 'ops',
      items: [
        { path: '/admin/notifications', label: 'Notifications', icon: 'notifications', capability: null },
        { path: '/admin/ops', label: 'Ops', icon: 'ops', capability: null },
      ],
    },
    {
      id: 'platform',
      label: 'Platform',
      icon: 'settings',
      items: [
        { path: '/admin/modules', label: 'Module Governance', icon: 'settings', capability: null },
        { path: '/admin/api-controller', label: 'Integrations', icon: 'integrations', capability: 'admin:integrations:read' },
        { path: '/admin/audit', label: 'Audit', icon: 'audit', capability: 'admin:audit:read' },
        { path: '/admin/deploy', label: 'Deploy', icon: 'rocket', superadminOnly: true },
      ],
    },
    {
      id: 'data-sources',
      label: 'Data Sources',
      icon: 'data',
      items: [
        { path: '/admin/hostaway/status', label: 'Hostaway Status', icon: 'data', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/promotion', label: 'Hostaway Promotion', icon: 'ops', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/properties', label: 'Hostaway Properties', icon: 'home', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/reservations', label: 'Hostaway Reservations', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/clc-reservations', label: 'CLC Reservations', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/tax-review', label: 'Tax Review', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/financials/fresh-coverage', label: 'Fresh Coverage', icon: 'data', capability: 'admin:integrations:read' },
        { path: '/admin/financials/fresh-pending', label: 'Fresh Pending', icon: 'data', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/availability', label: 'Hostaway Availability', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/reviews', label: 'Hostaway Reviews', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/hostaway/guest-payment-charges', label: 'Hostaway Guest Payments', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/link-manager', label: 'Property Documents', icon: 'data', capability: 'admin:integrations:read' },
        { path: '/admin/ezcare', label: 'EZCare', icon: 'work', capability: 'admin:integrations:read' },
        { path: '/admin/communications', label: 'Communications', icon: 'communications', capability: 'admin:communications:read' },
        { path: '/admin/communications/history', label: 'Communications History', icon: 'audit', capability: 'admin:communications:audit' },
        { path: '/admin/communications/inbox', label: 'Communications Inbox', icon: 'notifications', capability: 'admin:communications:inbox:read' },
      ],
    },
    {
      id: 'access',
      label: 'Access',
      icon: 'users',
      items: [
        { path: '/admin/users', label: 'Users', icon: 'users', capability: 'admin:rbac:read' },
        { path: '/admin/roles', label: 'Roles', icon: 'roles', capability: 'admin:rbac:read' },
        { path: '/admin/api-keys', label: 'API Keys', icon: 'api-keys', capability: 'admin:api-keys:read' },
      ],
    },
  ],
};

export default function AdminSidebar({ sidebarOpen = false, onClose, compact = false, onCompactChange }) {
  const location = useLocation();
  const { has, loading, isSuperAdmin } = useCapabilities();
  // Default-collapse less-used groups to reduce scroll
  const [expandedGroups, setExpandedGroups] = useState({
    operations: true,
    platform: true,
    'data-sources': false,
    access: false,
  });
  const [focusedIndex, setFocusedIndex] = useState(null);
  
  const handleCompactToggle = () => {
    if (onCompactChange) {
      onCompactChange(!compact);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Filter nav items based on capabilities
  const filterItems = (items) => {
    return items.filter((item) => {
      if (item.superadminOnly) {
        if (loading) return true;
        return isSuperAdmin === true;
      }
      if (!item.capability) return true;
      if (loading) return true;
      return has(item.capability);
    });
  };

  // Filter groups - hide if no accessible items
  const filterGroups = (groups) => {
    return groups.filter((group) => {
      const accessibleItems = filterItems(group.items);
      return accessibleItems.length > 0;
    });
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const visiblePinned = filterItems(navStructure.pinned);
  const visibleGroups = filterGroups(navStructure.groups);
  const isModulePath = (path) => path.startsWith('/modules/');

  // Auto-expand groups that contain the active route
  useEffect(() => {
    visibleGroups.forEach(group => {
      const accessibleItems = filterItems(group.items);
      const hasActiveItem = accessibleItems.some(item => isActive(item.path));
      
      if (hasActiveItem && !expandedGroups[group.id]) {
        setExpandedGroups(prev => ({
          ...prev,
          [group.id]: true,
        }));
      }
    });
  }, [location.pathname, visibleGroups, expandedGroups, filterItems, isActive]);

  // Debug instrumentation: Log nav items when nav_debug=1 query param is present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('nav_debug') === '1') {
      const debugGroups = visibleGroups.map(group => ({
        id: group.id,
        label: group.label,
        expanded: expandedGroups[group.id],
        accessibleItems: filterItems(group.items).map(item => ({
          path: item.path,
          label: item.label,
          icon: item.icon,
          capability: item.capability,
          hasCapability: item.capability ? has(item.capability) : true,
        })),
        allItems: group.items.map(item => ({
          path: item.path,
          label: item.label,
          capability: item.capability,
        })),
      }));
      const dataGroupItems = navStructure.groups.find(g => g.id === 'data')?.items || [];
      console.log('[AdminSidebar Debug] Navigation structure:', {
        location: location.pathname,
        visibleGroups: debugGroups,
        dataGroupItems: dataGroupItems.map(item => ({
          path: item.path,
          label: item.label,
          capability: item.capability,
        })),
        hasAdminIntegrationsRead: has('admin:integrations:read'),
        reviewsInDataGroup: dataGroupItems.some(item => item.path === '/admin/hostaway/reviews'),
        reviewsInAccessibleItems: debugGroups.find(g => g.id === 'data')?.accessibleItems.some(item => item.path === '/admin/hostaway/reviews'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, visibleGroups, expandedGroups]);

  return (
    <nav 
      className={`admin-sidebar ${sidebarOpen ? 'open' : ''} ${compact ? 'admin-sidebar-compact' : ''}`}
      aria-label="Admin panel navigation"
    >
      <div className="admin-sidebar-header">
        {!compact && (
          <div className="admin-sidebar-header-meta">
            <div className="admin-sidebar-header-kicker">Navigation</div>
            <div className="admin-sidebar-header-title">CLC Admin</div>
          </div>
        )}
        <button
          className="admin-sidebar-compact-toggle"
          onClick={handleCompactToggle}
          aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={compact ? 'chevronRight' : 'chevronRight'} size="sm" style={{ transform: compact ? 'none' : 'rotate(180deg)' }} />
        </button>
      </div>

      <div className="admin-sidebar-nav-content">
        {/* Pinned Items */}
        {!compact && <div className="admin-sidebar-section-label">Workspaces</div>}
        <ul className="admin-sidebar-nav-list" role="list">
          {visiblePinned.map((item, index) => {
            const active = isActive(item.path);
            return (
              <li key={item.path} className="admin-sidebar-nav-item">
                <Link
                  to={item.path}
                  className={`admin-sidebar-nav-link ${active ? 'admin-sidebar-nav-link-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  aria-label={active ? `${item.label} (current page)` : item.label}
                  title={item.label}
                  target={isModulePath(item.path) ? '_blank' : undefined}
                  rel={isModulePath(item.path) ? 'noopener noreferrer' : undefined}
                  onFocus={() => setFocusedIndex(`pinned-${index}`)}
                  onBlur={() => setFocusedIndex(null)}
                >
                  <span className="admin-sidebar-nav-icon">
                    <Icon name={item.icon} size="md" ariaLabel={item.label} />
                  </span>
                  {!compact && <span>{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Grouped Sections */}
        {!compact && <div className="admin-sidebar-section-label">Manage</div>}
        {visibleGroups.map((group) => {
          const accessibleItems = filterItems(group.items);
          if (accessibleItems.length === 0) return null;

          const isExpanded = expandedGroups[group.id];
          const hasActiveItem = accessibleItems.some(item => isActive(item.path));
          
          // Auto-expand if active item is in this group (but respect user's manual toggle)
          const shouldExpand = isExpanded || (hasActiveItem && !expandedGroups.hasOwnProperty(group.id + '_manual'));

          return (
            <div key={group.id} className="admin-sidebar-group">
              <button
                onClick={() => toggleGroup(group.id)}
                className={`admin-sidebar-group-header ${hasActiveItem ? 'admin-sidebar-group-header-active' : ''}`}
                aria-expanded={isExpanded}
                aria-label={`${group.label} section, ${isExpanded ? 'expanded' : 'collapsed'}`}
                title={group.label}
              >
                <span className="admin-sidebar-group-head">
                  <span className="admin-sidebar-group-head-icon">
                    <Icon name={group.icon || 'settings'} size="sm" ariaLabel={group.label} />
                  </span>
                  <span className="admin-sidebar-group-label">{group.label}</span>
                </span>
                <span className="admin-sidebar-group-chevron">
                  <Icon name={isExpanded ? 'chevronDown' : 'chevronRight'} size="sm" />
                </span>
              </button>
              {isExpanded && (
                <ul className="admin-sidebar-nav-list" role="list">
                  {accessibleItems.map((item, index) => {
                    const active = isActive(item.path);
                    return (
                      <li key={item.path} className="admin-sidebar-nav-item">
                        <Link
                          to={item.path}
                          className={`admin-sidebar-nav-link admin-sidebar-nav-link-nested ${active ? 'admin-sidebar-nav-link-active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                          aria-label={active ? `${item.label} (current page)` : item.label}
                          title={item.label}
                          onFocus={() => setFocusedIndex(`${group.id}-${index}`)}
                          onBlur={() => setFocusedIndex(null)}
                        >
                          <span className="admin-sidebar-nav-icon">
                            <Icon name={item.icon} size="sm" ariaLabel={item.label} />
                          </span>
                          {!compact && <span>{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

// Styles removed - now using CSS classes from admin-layout.css
